import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { BREAKPOINTS } from "@/lib/utils/responsive";

export const REIMBURSEMENT_FORM_STICKY_TOP_OFFSET = 16;
export const REIMBURSEMENT_FORM_STICKY_TOP_OFFSET_MOBILE = 56;

export function isBelowLgViewport(
  width = typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.lg,
): boolean {
  return width < BREAKPOINTS.lg;
}

export function getReimbursementFormStickyTopOffset(
  width = typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.lg,
): number {
  return isBelowLgViewport(width)
    ? REIMBURSEMENT_FORM_STICKY_TOP_OFFSET_MOBILE
    : REIMBURSEMENT_FORM_STICKY_TOP_OFFSET;
}

export function getReimbursementFormStickyZIndex(
  width = typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.lg,
): number {
  return isBelowLgViewport(width) ? 10 : 30;
}

export type StickyMode = "natural" | "pinned" | "bottom";

export interface ReimbursementFormStickyState {
  boundaryRef: RefObject<HTMLDivElement | null>;
  columnRef: RefObject<HTMLDivElement | null>;
  asideRef: RefObject<HTMLElement | null>;
  mode: StickyMode;
  asideHeight: number;
  fixedStyle: CSSProperties;
}

export function computeStickyMode(
  boundaryRect: Pick<DOMRect, "top" | "bottom">,
  asideHeight: number,
  topOffset: number,
): StickyMode {
  if (asideHeight <= 0) return "natural";
  if (boundaryRect.top > topOffset) return "natural";
  if (boundaryRect.bottom <= topOffset + asideHeight) return "bottom";
  return "pinned";
}

export function computeShouldPin(
  boundaryRect: Pick<DOMRect, "top" | "bottom">,
  asideHeight: number,
  topOffset: number,
): boolean {
  return computeStickyMode(boundaryRect, asideHeight, topOffset) === "pinned";
}

export function getPinnedAsideFixedStyle(
  asideRect: Pick<DOMRect, "left" | "width">,
  topOffset: number,
  zIndex: number,
): CSSProperties {
  return {
    position: "fixed",
    top: topOffset,
    left: asideRect.left,
    width: asideRect.width,
    zIndex,
  };
}

export function getBottomAsideFixedStyle(
  boundaryRect: Pick<DOMRect, "bottom">,
  asideRect: Pick<DOMRect, "left" | "width">,
  asideHeight: number,
  zIndex: number,
): CSSProperties {
  return {
    position: "fixed",
    top: boundaryRect.bottom - asideHeight,
    left: asideRect.left,
    width: asideRect.width,
    zIndex,
  };
}

/**
 * Horizontal pin geometry must come from the in-flow column, not the aside.
 * Once pinned, the aside is `position: fixed` with an inline width, so reading
 * its own rect after zoom/resize reuses the stale pixel size until scroll.
 */
export function getAsideHorizontalRect(
  columnRect: Pick<DOMRect, "left" | "width"> | null | undefined,
  asideRect: Pick<DOMRect, "left" | "width">,
): Pick<DOMRect, "left" | "width"> {
  if (columnRect && columnRect.width > 0) {
    return { left: columnRect.left, width: columnRect.width };
  }
  return { left: asideRect.left, width: asideRect.width };
}

export function useReimbursementFormSticky(
  enabled = true,
): ReimbursementFormStickyState {
  const boundaryRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const [state, setState] = useState<{
    mode: StickyMode;
    asideHeight: number;
    fixedStyle: CSSProperties;
  }>({
    mode: "natural",
    asideHeight: 0,
    fixedStyle: {},
  });

  useEffect(() => {
    if (!enabled) return;

    let resizeObserver: ResizeObserver | null = null;

    const update = () => {
      const boundary = boundaryRef.current;
      const column = columnRef.current;
      const aside = asideRef.current;
      if (!boundary || !column || !aside) return;

      const topOffset = getReimbursementFormStickyTopOffset();
      const zIndex = getReimbursementFormStickyZIndex();
      const boundaryRect = boundary.getBoundingClientRect();
      const columnRect = column.getBoundingClientRect();
      const asideRect = aside.getBoundingClientRect();
      const horizontalRect = getAsideHorizontalRect(columnRect, asideRect);
      const asideHeight = aside.offsetHeight;
      const mode = computeStickyMode(boundaryRect, asideHeight, topOffset);

      if (mode === "pinned") {
        setState({
          mode,
          asideHeight,
          fixedStyle: getPinnedAsideFixedStyle(horizontalRect, topOffset, zIndex),
        });
        return;
      }

      if (mode === "bottom") {
        if (isBelowLgViewport()) {
          setState({
            mode,
            asideHeight,
            fixedStyle: getBottomAsideFixedStyle(
              boundaryRect,
              horizontalRect,
              asideHeight,
              zIndex,
            ),
          });
        } else {
          setState({
            mode,
            asideHeight,
            fixedStyle: {},
          });
        }
        return;
      }

      setState({
        mode: "natural",
        asideHeight: 0,
        fixedStyle: {},
      });
    };

    const setupResizeObserver = () => {
      const boundary = boundaryRef.current;
      const column = columnRef.current;
      const aside = asideRef.current;
      if (!boundary || !column || !aside || typeof ResizeObserver === "undefined")
        return;

      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(boundary);
      resizeObserver.observe(column);
      resizeObserver.observe(aside);
    };

    update();
    setupResizeObserver();

    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);

    const rafId = window.requestAnimationFrame(() => {
      update();
      setupResizeObserver();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
      resizeObserver?.disconnect();
    };
  }, [enabled]);

  return {
    boundaryRef,
    columnRef,
    asideRef,
    mode: enabled ? state.mode : "natural",
    asideHeight: enabled ? state.asideHeight : 0,
    fixedStyle: enabled ? state.fixedStyle : {},
  };
}
